<template>
  <section id="stepItemSection">
    <div v-if="progress" class="absolute top-20 md:top-5 left-1/2 flex -translate-x-1/2 items-center gap-4 self-center text-slate-400 sm:mr-3">
      <AnimatedProgressBar :percentage="animatedProgressAmount" class="w-52 md:w-80" progress-bar-height="0.5rem"/>
      <p class="whitespace-nowrap text-sm">{{progress * 100}}% Complete</p>
    </div>
    <StepItemFormatForm
      v-if="format === 'form'"
      :btn-size="btnSize"
      :is-ready-for-next-step="isReadyForNextStep"
      :title="title"
      :subtitle="subtitle"
      :can-skip="canSkip"
      :show-lock="showLock"
      :loading="loading"
      :max-width-class="formMaxWidthClass"
      :can-go-to-prev-step="canGoToPrevStep"
      :without-buttons="withoutButtons"
      :subtitle-is-button="subtitleIsButton"
      @handle-next-step="handleNextStep"
      @handle-prev-step="handlePrevStep"
      @subtitle-click="handleSubtitleClick"
    >
      <slot/>
      <template v-slot:next-step-text>
        <slot name="next-step-text"/>
      </template>
    </StepItemFormatForm>
    <StepItemFormatSplash1
      v-else-if="format === 'splash'"
      :is-ready-for-next-step="isReadyForNextStep"
      :title="title"
      :subtitle="subtitle"
      :can-skip="canSkip"
      :show-lock="showLock"
      :loading="loading"
      :snooze="snooze"
      :can-go-to-prev-step="canGoToPrevStep"
      @handle-next-step="handleNextStep"
      @handle-prev-step="handlePrevStep"
    >
      <slot/>
      <template v-slot:splash-image>
        <slot name="splash-image"/>
      </template>
      <template v-slot:next-step-text>
        <slot name="next-step-text"/>
      </template>
      <template v-slot:footer-text>
        <slot name="footer-text"/>
      </template>
    </StepItemFormatSplash1>
    <StepItemFormatSplash2
      v-else-if="format === 'splash2'"
      :is-ready-for-next-step="isReadyForNextStep"
      :title="title"
      :subtitle="subtitle"
      :can-skip="canSkip"
      :show-lock="showLock"
      :loading="loading"
      :can-go-to-prev-step="canGoToPrevStep"
      @handle-next-step="handleNextStep"
      @handle-prev-step="handlePrevStep"
    >
      <slot/>
      <template v-slot:splash-image>
        <slot name="splash-image"/>
      </template>
      <template v-slot:next-step-text>
        <slot name="next-step-text"/>
      </template>
    </StepItemFormatSplash2>
  </section>
</template>

<script setup>
import StepItemFormatForm from "./StepItemFormatForm.vue";
import StepItemFormatSplash1 from "./StepItemFormatSplash1.vue";
import StepItemFormatSplash2 from "./StepItemFormatSplash2.vue";
import AnimatedProgressBar from "./AnimatedProgressBar.vue";
import {onActivated, onDeactivated, ref} from "vue";

const props = defineProps({
  format: {
    type: String,
    required: true,
    default() {
      return "form";
    },
    validator(value) {
      return ["form", "splash", "splash2"].includes(value);
    },
  },
  isReadyForNextStep: {
    type: Boolean,
    required: true,
  },
  snooze: {
    type: Function,
    default: null
  },
  btnSize: String,
  canGoToPrevStep: Boolean,
  subtitleIsButton: Boolean,
  title: String,
  subtitle: String,
  canSkip: Boolean,
  showLock: Boolean,
  progress: Number,
  loading: Boolean,
  formMaxWidthClass: String,
  withoutButtons: Boolean,
});

const emits = defineEmits(["handle-next-step", "handle-prev-step", "subtitle-click"]);
const animatedProgressAmount = ref(0);

const handleNextStep = (wasSkipped) => {
  emits("handle-next-step", wasSkipped);
};

const handlePrevStep = () => {
  emits("handle-prev-step");
};

const handleSubtitleClick = () => {
  emits("subtitle-click");
};

onActivated(() => {
  if(props.progress) {
    setTimeout(() => {
      animatedProgressAmount.value = props.progress;
    }, 100);
  }
});

onDeactivated(() => {
  if(props.progress){
    animatedProgressAmount.value = 0;
  }
});
</script>
